import React, { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"

import { EDIT, DELETE } from "const"
import { hasAccess, hasProjectAccess } from "client/utils"
import {
    PRM_DRIVE,
    StoreType,
    DriveActionType,

    setDriveAction,
    isRecycleOpened,
    isProjectFolderOpened,
    clearDriveSelection,

    DRIVE_ACTION_RENAME,
    DRIVE_ACTION_MOVE,
    DRIVE_ACTION_RESTORE,
    DRIVE_ACTION_DELETE,
    closeModal,
} from "client/store"
import { ContentColumn } from "client/parts/layout"
import { ModalContent } from "client/parts/modal/styled"
import {
    CrossIcon,
    OpenIcon,
    RenameIcon,
    MoveIcon,
    RemoveIcon,
    CheckIcon,
} from "client/components"
import {
    ToolbarWrapper,
    ToolbarTotal,
    ToolbarActionPanel,
    ToolbarAction,
    ToolbarClose,
} from "./styled"

import { BreadCrumbWrapper } from "../breadCrumbs/styled"
import { OpenFolder, OpenFile } from "../actions/open"

const Toolbar = () => {
    const drivePath = useSelector((state: StoreType) => state.drive.drivePath)
    const baseRoute = useSelector((state: StoreType) => state.drive.baseRoute)
    const onSelectFile = useSelector((state: StoreType) => state.drive.onSelectFile)
    const onSelectFiles = useSelector((state: StoreType) => state.drive.onSelectFiles)
    const onSelectVersion = useSelector((state: StoreType) => state.drive.onSelectVersion)
    const onSelect = onSelectFile || onSelectFiles || onSelectVersion

    const folderActive = useSelector((state: StoreType) => state.drive.folder.active)
    const foldersSelected = useSelector((state: StoreType) => state.drive.folder.selected)

    const fileActive = useSelector((state: StoreType) => state.drive.file.active)
    const filesSelected = useSelector((state: StoreType) => state.drive.file.selected)

    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [menuItems, setMenuItems] = useState<any[]>([])

    const selectedCount = (folderActive || fileActive) ? 1 : foldersSelected.length + filesSelected.length

    const singleFolderSelected = foldersSelected.length === 1 && filesSelected.length === 0
    const singleFileSelected = foldersSelected.length === 0 && filesSelected.length === 1

    const folder = folderActive || (singleFolderSelected && foldersSelected[0]) || null
    const file = fileActive || (singleFileSelected && filesSelected[0]) || null

    const recycleOpened = isRecycleOpened(drivePath)
    const projectId = isProjectFolderOpened(drivePath)

    const accountEditAccess = hasAccess(PRM_DRIVE, EDIT)
    const projectEditAccess = hasProjectAccess(PRM_DRIVE, EDIT, projectId)
    const editAccess = !projectId
        ? accountEditAccess
        : projectEditAccess

    const accountDeleteAccess = hasAccess(PRM_DRIVE, DELETE)
    const projectDeleteAccess = hasProjectAccess(PRM_DRIVE, DELETE, projectId)
    const deleteAccess = !projectId
        ? accountDeleteAccess
        : projectDeleteAccess


    // toolbar is displayed over the breadcrumbs, so the correct position will be determined by the breadcrumbs
    const breadCrumbs = document.querySelector(BreadCrumbWrapper)

    useEffect(() => {
        fixToolbarOnScroll(null, true)

        // drive is opened in Modal or in a full-screen view
        const scrollableParent = breadCrumbs.closest(ModalContent) || document.body.querySelector(ContentColumn)
        scrollableParent.addEventListener("scroll", fixToolbarOnScroll)
        return () => scrollableParent.removeEventListener("scroll", fixToolbarOnScroll)
    }, [])

    const fixToolbarOnScroll = (e?: MouseEvent, forceDisplay?: boolean) => {
        const toolbar = document.querySelector(ToolbarWrapper)

        if (!toolbar) return
        const breadCrumbsBounding = breadCrumbs.getBoundingClientRect()
        const offsetTop = breadCrumbsBounding.top

        if (offsetTop > 0) { // when the page is scrolled up move toolbar to its place
            MoveToolbar(toolbar, false)
        } else if (offsetTop < 0) { // when the page is scrolled down
            MoveToolbar(toolbar, true)
        } else if (forceDisplay) {
            MoveToolbar(toolbar, true)
        }
    }

    useEffect(() => {
        if (!recycleOpened) setMenuItems(createMenu())
        if (recycleOpened) setMenuItems(createRecycleMenu())
    }, [folderActive, fileActive, foldersSelected, filesSelected])

    const menuClick = (e: React.MouseEvent, action: DriveActionType) => {
        e.stopPropagation()
        dispatch(setDriveAction(action))
    }

    const openFolderItem = (
        <ToolbarAction
            key="openFolder"
            onClick={() => OpenFolder(folder, userData, onSelect, dispatch)}
        >
            <OpenIcon />
            <span>{lang.open}</span>
        </ToolbarAction>
    )
    const openFileItem = (
        <ToolbarAction
            key="openFile"
            onClick={() => OpenFile(file.id, file.url, drivePath, baseRoute, onSelect, dispatch)}
        >
            <OpenIcon />
            <span>{lang.details}</span>
        </ToolbarAction>
    )
    const renameItem = (
        <ToolbarAction key="rename" onClick={(e) => menuClick(e, DRIVE_ACTION_RENAME)}>
            <RenameIcon />
            <span>{lang.rename}</span>
        </ToolbarAction>
    )
    const moveItem = (
        <ToolbarAction key="move" onClick={(e) => menuClick(e, DRIVE_ACTION_MOVE)}>
            <MoveIcon />
            <span>{lang.move}</span>
        </ToolbarAction>
    )
    const restoreItem = (
        <ToolbarAction key="restore" onClick={(e) => menuClick(e, DRIVE_ACTION_RESTORE)}>
            <span>{lang.restore}</span>
        </ToolbarAction>
    )
    const deleteItem = (
        <ToolbarAction key="delete" onClick={(e) => menuClick(e, DRIVE_ACTION_DELETE)}>
            <RemoveIcon />
            <span>{lang.delete}</span>
        </ToolbarAction>
    )
    const selectItem = (
        <ToolbarAction
            key="select"
            onClick={() => {
                if (onSelectFile) onSelectFile(file)
                else if (onSelectFiles) onSelectFiles(filesSelected.length > 0 ? filesSelected : [file])
                else if (onSelectVersion) onSelectVersion(file.versions && file.versions[0], file)
                dispatch(closeModal()) // close drive list modal
            }}
        >
            <CheckIcon />
            <span>{lang.select}</span>
        </ToolbarAction>
    )

    const createMenu = () => {
        const menu: any[] = []

        // project and QID folders can be only opened
        if (folder && (folder.project || folder.product)) {
            menu.push(openFolderItem)
        } else {
            if (
                (file && (onSelectFile || onSelectVersion))
                || ((file || filesSelected.length > 0) && onSelectFiles)
            ) menu.push(selectItem)

            if (folder) menu.push(openFolderItem)
            if (file) menu.push(openFileItem)

            if (editAccess && (folder || file)) menu.push(renameItem)

            if (editAccess) menu.push(moveItem)
            if (deleteAccess) menu.push(deleteItem)
        }

        return menu
    }

    const createRecycleMenu = () => {
        const menu: any[] = []

        if (folder) menu.push(openFolderItem)
        if (file) menu.push(openFileItem)

        if (editAccess) menu.push(restoreItem)
        if (deleteAccess) menu.push(deleteItem)

        return menu
    }

    return (
        <ToolbarWrapper onClick={(e) => e.stopPropagation()}>
            <ToolbarTotal>{`${lang.selected}: ${selectedCount}`}</ToolbarTotal>
            <ToolbarActionPanel>
                {
                    menuItems.map((item) => (item))
                }
            </ToolbarActionPanel>
            <ToolbarClose onClick={() => clearDriveSelection(dispatch)}>
                <CrossIcon />
            </ToolbarClose>
        </ToolbarWrapper>
    )
}
export default Toolbar


let moveInProgress = false
const onTopClass = "toolbar-on-top"

const MoveToolbar = (element: Element, displayOnTop: boolean) => {
    if (moveInProgress) return
    const toolbar = element as HTMLElement

    const hasClass = toolbar.classList.contains(onTopClass)
    const toolbarBounding = toolbar.getBoundingClientRect()

    if (displayOnTop && !hasClass) {
        moveInProgress = true
        toolbar.classList.add(onTopClass)
        toolbar.style.width = `${toolbarBounding.width}px`
        toolbar.style.transform = "translateY(-100%)"

        setTimeout(() => {
            toolbar.style.transform = "translateY(0)"
            toolbar.style.transition = "transform 0.2s ease"

            moveInProgress = false
        }, 200)
    }

    if (!displayOnTop && hasClass) {
        toolbar.classList.remove(onTopClass)
        toolbar.removeAttribute("style")
    }
}
